import { LiaRupeeSignSolid } from "react-icons/lia";
import { useState, useEffect } from "react";
import {
  FaCheckCircle,
  FaShippingFast,
  FaBoxOpen,
  FaGem,
  FaGlobe,
  FaChevronUp,
  FaChevronDown,
  FaBell,
} from "react-icons/fa";

import { useGetCart } from "../../../queries/useCartQuery";

import {
  Button,
  HeadingTitle,
  Loader,
  Ratings,
  SubHeadingTitle,
} from "../../../components/shared/index";

import styles from "./productdetails.module.css";
import toast from "react-hot-toast";
import authService from "../../../services/authService";
import { useNavigate } from "react-router-dom";
import productStockNotificationService from "../../../services/productNotifyService";

const ProductInfo = ({
  productData,
  addToCart,
  isPending,
  isCartPending,
  isError,
  error,
}) => {
  const navigate = useNavigate();

  const isLoggedIn = authService.isLoggedIn();

  const { data: cartData } = useGetCart();

  // ==============================
  // STATES
  // ==============================
  const [quantity, setQuantity] = useState(1);

  // Guest Notify popup
  const [showNotifyModal, setShowNotifyModal] = useState(false);

  // Email / phone entered by guest
  const [notifyContact, setNotifyContact] = useState("");

  // API loading
  const [isNotifyPending, setIsNotifyPending] = useState(false);

  // After successful notification subscription
  const [isNotified, setIsNotified] = useState(false);

  // ==============================
  // CHECK CART
  // ==============================
  const isAlreadyInCart = cartData?.some(
    (item) => item.product_id === productData?.id
  );

  // ==============================
  // ADD TO CART
  // ==============================
  const addItemToCart = async () => {
    if (!isLoggedIn) {
      toast.error("⚠️ Please login first");
      return false;
    }

    if (productData?.qty < 1) {
      toast.error("❌ Product is out of stock");
      return false;
    }

    try {
      await addToCart({
        product_id: productData?.id,
        quantity: quantity,
      });

      toast.success("Item added to cart");

      return true;
    } catch (error) {
      toast.error("Something went wrong");
      return false;
    }
  };

  // ==============================
  // HANDLE ADD TO CART
  // ==============================
  const handleAddToCart = async () => {
    if (isAlreadyInCart) {
      toast.error("Product already added to cart");
      return;
    }

    await addItemToCart();
  };

  // ==============================
  // BUY NOW
  // ==============================
  const handleBuyNow = () => {
    if (productData?.qty < 1) {
      toast.error("❌ Product is out of stock");
      return;
    }

    navigate("/checkout/payment", {
      state: {
        buyNow: true,
        product: productData,
        quantity,
      },
    });
  };

  // ==============================
  // RESET WHEN PRODUCT CHANGES
  // ==============================
  useEffect(() => {
    setQuantity(1);
    setShowNotifyModal(false);
    setNotifyContact("");
    setIsNotified(false);
  }, [productData?.id]);

  // ==============================
  // INCREASE QUANTITY
  // ==============================
  const increaseQty = () => {
    if (quantity < productData?.qty) {
      setQuantity((prev) => prev + 1);
    }
  };

  // ==============================
  // DECREASE QUANTITY
  // ==============================
  const decreaseQty = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  // ============================================================
  // NOTIFY ME BUTTON
  // ============================================================
  const handleNotifyMe = async () => {
    const loggedIn = authService.isLoggedIn();

    // ==========================================================
    // LOGGED-IN USER
    // API:
    // POST /api/product-stock-notifications
    //
    // Body:
    // {
    //    "product_id": 6
    // }
    //
    // Token will be passed by apiService
    // ==========================================================
    if (loggedIn) {
      try {
        setIsNotifyPending(true);

        const response =
          await productStockNotificationService.subscribe({
            productId: productData?.id,
          });

        toast.success(
          response?.message ||
            "You will be notified when this product is back in stock."
        );

        setIsNotified(true);
      } catch (error) {
        toast.error(
          error?.message ||
            "Unable to subscribe for notification"
        );
      } finally {
        setIsNotifyPending(false);
      }

      return;
    }

    // ==========================================================
    // GUEST USER
    // Don't call API immediately.
    // Open popup first.
    // ==========================================================
    setNotifyContact("");
    setShowNotifyModal(true);
  };

  // ============================================================
  // GUEST NOTIFY SUBMIT
  // API will be called ONLY after clicking "Notify Me"
  // inside popup.
  // ============================================================
 const handleGuestNotifySubmit = async (e) => {
  e.preventDefault();

  const value = notifyContact.trim();

  // Empty
  if (!value) {
    toast.error("Please enter your email or phone number");
    return;
  }

  // Check email
  const isEmail =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  // Check 10 digit phone
  const isPhone =
    /^[0-9]{10}$/.test(value);

  // Invalid
  if (!isEmail && !isPhone) {
    toast.error(
      "Please enter a valid email or 10-digit phone number"
    );
    return;
  }

  try {
    setIsNotifyPending(true);

    // =========================================
    // GUEST NOTIFICATION API
    // =========================================
    const response =
      await productStockNotificationService.subscribe({
        productId: productData?.id,
        email: isEmail ? value : undefined,
        phone: isPhone ? value : undefined,
      });

    // =========================================
    // SUCCESS
    // =========================================
    toast.success(
      response?.message ||
        "You will be notified when this product is back in stock."
    );

    setIsNotified(true);

    // Close popup
    setShowNotifyModal(false);

    // Clear input
    setNotifyContact("");

  } catch (error) {
    toast.error(
      error?.message ||
        "Unable to subscribe for notification"
    );
  } finally {
    setIsNotifyPending(false);
  }
};

  return (
    <>
      {/* =====================================================
          RIGHT SIDE PRODUCT INFORMATION
      ===================================================== */}
      <div className={styles.rightSection}>

        {/* ==============================
            LOADING
        ============================== */}
        {isPending ? (
          <div className={styles.productInfoLoader}>
            <Loader title="Loading product details..." />
          </div>
        ) : isError ? (
          /* ==============================
             ERROR
          ============================== */
          <SubHeadingTitle
            style={styles.errorMessage}
            subTitle={
              error?.message ||
              "Failed to load product details"
            }
          />
        ) : (
          <>
            {/* ==============================
                PRODUCT NAME
            ============================== */}
            <HeadingTitle
              text={productData?.name || ""}
              style={styles.heading}
            />

            {/* ==============================
                RATINGS
            ============================== */}
            <Ratings rating={productData?.rating || 0} />

            {/* ==============================
                PRICE + SKU
            ============================== */}
            <div className={styles.metaInfo}>

              {/* PRICE */}
              <div className={styles.priceWrapper}>

                {/* OFFER PRICE */}
                {productData?.offer_price &&
                productData.offer_price > 0 ? (
                  <>
                    <h3 className={styles.offerPrice}>
                      <LiaRupeeSignSolid
                        className={styles.priceIcon}
                        size={24}
                      />

                      {productData.offer_price.toLocaleString()}
                      /-
                    </h3>

                    {/* ORIGINAL PRICE */}
                    <h4 className={styles.originalPrice}>
                      <LiaRupeeSignSolid
                        className={styles.priceIcon}
                        size={18}
                      />

                      {productData.price.toLocaleString()}
                    </h4>

                    {/* DISCOUNT */}
                    <span className={styles.discountText}>
                      {Math.round(
                        ((productData.price -
                          productData.offer_price) /
                          productData.price) *
                          100
                      )}
                      % off
                    </span>
                  </>
                ) : (
                  /* NORMAL PRICE */
                  <h3 className={styles.offerPrice}>
                    <LiaRupeeSignSolid
                      className={styles.priceIcon}
                      size={24}
                    />

                    {productData?.price?.toLocaleString()}
                    /-
                  </h3>
                )}
              </div>

              {/* ==============================
                  SKU
              ============================== */}
              <SubHeadingTitle
                style={styles.sku}
                subTitle={
                  <>
                    <span>SKU : </span>

                    <span>
                      {productData?.sku || "N/A"}
                    </span>
                  </>
                }
              />
            </div>

            {/* ==================================================
                QUANTITY
                Only show when product is in stock
            ================================================== */}
            {productData?.qty > 0 && (
              <div className={styles.qtyRow}>

                <span className={styles.qtyTitle}>
                  Quantity :
                </span>

                <div className={styles.qtyBox}>

                  {/* CURRENT QUANTITY */}
                  <span className={styles.qtyNumber}>
                    {quantity}
                  </span>

                  {/* ARROWS */}
                  <div className={styles.qtyArrows}>

                    {/* INCREASE */}
                    <button
                      type="button"
                      onClick={increaseQty}
                      disabled={
                        quantity === productData?.qty
                      }
                    >
                      <FaChevronUp size={12} />
                    </button>

                    {/* DECREASE */}
                    <button
                      type="button"
                      onClick={decreaseQty}
                      disabled={quantity === 1}
                    >
                      <FaChevronDown size={12} />
                    </button>

                  </div>
                </div>
              </div>
            )}

            {/* ==================================================
                AVAILABILITY
            ================================================== */}
            <SubHeadingTitle
              subTitle={
                <>
                  <span>Availability: </span>

                  <span
                    className={
                      productData?.qty > 0
                        ? styles.inStock
                        : styles.outOfStock
                    }
                  >
                    {productData?.qty > 0
                      ? "In Stock"
                      : "Out of Stock"}
                  </span>
                </>
              }
            />

            {/* ==================================================
                BUTTONS
            ================================================== */}
            <div className={styles.btnGroup}>

              {/* ============================================
                  IN STOCK
              ============================================ */}
              {productData?.qty > 0 ? (
                <>
                  {/* ADD TO CART */}
                  <Button
                    type="button"
                    title={
                      isCartPending
                        ? "Adding to Cart..."
                        : isAlreadyInCart
                        ? "Already in Cart"
                        : "Add To Cart"
                    }
                    style={styles.cartBtn}
                    disabled={
                      isCartPending ||
                      isAlreadyInCart
                    }
                    onHandler={handleAddToCart}
                  />

                  {/* BUY NOW */}
                  <Button
                    type="button"
                    title="Buy Now"
                    style={styles.buyBtn}
                    disabled={isCartPending}
                    onHandler={handleBuyNow}
                  />
                </>
              ) : (

                /* ==========================================
                   OUT OF STOCK
                   ========================================== */
                <button
                  type="button"
                  className={`${styles.notifyBtn} ${
                    isNotified
                      ? styles.notifyBtnSuccess
                      : ""
                  }`}
                  onClick={handleNotifyMe}
                  disabled={
                    isNotifyPending ||
                    isNotified
                  }
                >
                  <FaBell size={17} />

                  <span>
                    {isNotifyPending
                      ? "Subscribing..."
                      : isNotified
                      ? "Notification Enabled"
                      : "Notify Me When Available"}
                  </span>
                </button>
              )}
            </div>

            {/* ==================================================
                FEATURE LIST
            ================================================== */}
            <ul className={styles.featureList}>

              <li>
                <FaCheckCircle
                  size={22}
                  color="black"
                />

                COD, Easy Returns & Quick Exchanges
              </li>

              <li>
                <FaShippingFast
                  size={22}
                  color="black"
                />

                Express 24-Hour Delivery (Charges Apply)
              </li>

              <li>
                <FaBoxOpen
                  size={22}
                  color="black"
                />

                Free Shipping on Prepaid Orders Above ₹1000
              </li>

              <li>
                <FaGem
                  size={22}
                  color="black"
                />

                Premium Quality Stones, Plating & Handwork
              </li>

              <li>
                <FaGlobe
                  size={22}
                  color="black"
                />

                Trusted by 1.2M+ Customers Worldwide
              </li>

            </ul>
          </>
        )}
      </div>

      {/* =====================================================
          GUEST NOTIFY POPUP
          This popup appears ONLY when user is NOT logged in.
      ===================================================== */}
      {showNotifyModal && (
        <div
          className={styles.notifyModalOverlay}
          onClick={() =>
            setShowNotifyModal(false)
          }
        >
          <div
            className={styles.notifyModal}
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* ==============================
                CLOSE BUTTON
            ============================== */}
            <button
              type="button"
              className={styles.notifyClose}
              onClick={() =>
                setShowNotifyModal(false)
              }
            >
              ×
            </button>

            {/* ==============================
                BELL ICON
            ============================== */}
            <div className={styles.notifyIcon}>
              <FaBell size={24} />
            </div>

            {/* ==============================
                TITLE
            ============================== */}
            <h3>Get Notified</h3>

            {/* ==============================
                DESCRIPTION
            ============================== */}
            <p>
              This product is currently out of stock.
              <br />
              Enter your email or phone number and
              we'll notify you when it is back in stock.
            </p>

            {/* ==============================
                FORM
            ============================== */}
            <form
              onSubmit={handleGuestNotifySubmit}
            >
              {/* EMAIL / PHONE */}
              <input
                type="text"
                value={notifyContact}
                onChange={(e) =>
                  setNotifyContact(
                    e.target.value
                  )
                }
                placeholder="Enter email or phone number"
                className={styles.notifyInput}
                autoFocus
              />

              {/* ==============================
                  NOTIFY ME
              ============================== */}
              <button
                type="submit"
                className={styles.notifySubmitBtn}
                disabled={isNotifyPending}
              >
                <FaBell size={16} />

                {isNotifyPending
                  ? "Submitting..."
                  : "Notify Me"}
              </button>
            </form>

          </div>
        </div>
      )}
    </>
  );
};

export default ProductInfo;